import { useTranslation } from 'react-i18next';
import { Mail } from 'lucide-react';

/* ===========================================================================
 * THE ONE FOOTER — who built this, and how to reach them when it breaks.
 *
 * It exists because the only place the vendor was named was the sign-in
 * screen: the person who needs support is, by definition, already signed in
 * and looking at whatever went wrong, and there was nothing on that screen
 * telling them who to ask. A support address nobody can find is not a support
 * address.
 *
 * ONE SUPPORT ROUTE, AND IT IS THE ADDRESS (owner). A WhatsApp link sat beside
 * it until now. Two doors to the same desk is a choice the reader has to make
 * before they can ask their question, and the one that leaves a written trail
 * — with the screen, the employee code and the date in it — is the one support
 * can actually act on. The number is gone from the product rather than merely
 * hidden: a link nobody is meant to use is still a link somebody uses.
 *
 * WHERE IT SITS, AND WHY IT IS NOT INSIDE THE READING COLUMN. It is the LAST
 * CHILD OF `main`, a sibling of the padded wrapper that holds the outlet, and
 * it carries its own inset. It used to live inside that wrapper, which is the
 * reason it kept missing the floor: `mt-auto` pushes an item to the end of its
 * parent's CONTENT box, and the wrapper's bottom padding — a full page gutter —
 * was drawn below that, so the footer stopped a gutter short of the bottom no
 * matter how the height chain was arranged. (Two earlier passes chased the
 * height chain instead; the second cut the padding from 96px to 34 and left the
 * gap.) Out here the wrapper's bottom padding is the air ABOVE the footer,
 * where it belongs as separation from the content, and the block below the
 * footer is the footer's own.
 *
 * `mt-auto` stays, and `main` is a flex column, so the rule holds in both
 * directions: on a short screen the footer sits on the floor of the viewport
 * rather than floating under three inches of nothing, and on a long one it is
 * the last thing after the content, reached by scrolling.
 *
 * ALIGNED WITH THE RAIL'S BUILD STAMP (owner). Both are the same kind of line —
 * the quiet, permanent one at the bottom of a column — so both close with 8px:
 * `py-element` here against `AppVersion`'s `py-2`, on the same 12.5/12px step
 * and the same tight leading. The two now land on one floor line instead of the
 * footer sitting a gutter higher than the stamp beside it.
 *
 * THE PHONE'S BOTTOM NAVIGATION IS NOT THIS COMPONENT'S PROBLEM ANY MORE. It
 * carried `pb-24` to clear `MobileBottomNav`, which cleared the END of a scroll
 * and did nothing for the middle of one — a list still ran behind the capsule
 * on its way past. `AppShell` reserves the whole band outside the scrolling
 * column now (see its note), so the footer is just a footer again and spaces
 * itself the same at every width.
 *
 * `AppShell` keeps it off the WIDE routes — the full-bleed boards, whose
 * container is `h-full` and whose child owns the whole viewport; appending a
 * block to that column makes the board shorter than its own scrollport and
 * gives a map a scrollbar. Somebody on the map who needs support is one
 * navigation away from a screen with it.
 *
 * `rel="noreferrer"` on the vendor link, and no `target` on the `mailto:`: a
 * mail handler opens its own app, and forcing a tab as well leaves an empty
 * one behind.
 * ======================================================================== */

/** Where support actually lands. One definition, so both links agree. */
const VENDOR_URL = 'https://araneae.net';
const SUPPORT_EMAIL = 'it@araneae.net';

export function AppFooter(): JSX.Element {
  const { t } = useTranslation();
  const link =
    'inline-flex items-center gap-1.5 rounded-xs underline decoration-border underline-offset-2 ' +
    'transition-colors duration-fast ease-standard hover:text-foreground hover:decoration-current ' +
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

  return (
    /* THE SAME QUIET FLOW AS THE VERSION STAMP (owner). It was `text-copy-sm`
       over `pt-component` — a 13px line with a full component's worth of air
       above it, which made the page's least important sentence its tallest
       band. The rail's build stamp is the right reference: it says something
       operationally real in 12.5px on tight leading and takes almost no room.
       Same step, same leading, an element's gap instead of a component's.

       TWO BOXES, because the footer now carries its own inset. The outer one
       REPEATS THE SHELL WRAPPER'S BOX EXACTLY — `mx-auto max-w-content` plus
       the same `--gutter` pair — so the words and the hairline keep the width
       and the alignment they had inside it; only the block padding is this
       component's own, and only below the line. The inner one is the row. */
    <footer className="mt-auto mx-auto w-full max-w-content px-gutter-sm pb-element md:px-gutter">
      <div className="flex flex-wrap items-center gap-x-component gap-y-tight border-t border-border pt-element text-label font-normal leading-tight text-muted-foreground">
        <span>
          {t('common.developedByPrefix', 'Developed by')}{' '}
          <a className={link} href={VENDOR_URL} target="_blank" rel="noreferrer">
            araneae.net
          </a>
        </span>
        {/* `ms-auto` puts support on the opposite edge on a desktop and lets it
            wrap under on a phone. */}
        <a className={`${link} ms-auto`} href={`mailto:${SUPPORT_EMAIL}`} rel="noreferrer">
          <Mail size={13} aria-hidden />
          {SUPPORT_EMAIL}
        </a>
      </div>
    </footer>
  );
}
